import type {
  AppSelectOption,
  SelectPaginatedData,
} from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { AppSelectPaginatedFetcherParams } from '@shared/ui/selects'

import { useTutorsListApi } from '@entities/tutor'
import { useAppSelectPaginated } from '@shared/ui/selects'

import { useTutorBillingStore } from './store'

interface FilterTutorBillingTutorReturn {
  tutorIds: WritableComputedRef<number[] | undefined>
  tutorOptions: ComputedRef<AppSelectOption<number>[]>
  selectedTutorOptions: ComputedRef<AppSelectOption<number>[]>
  tutorHasMore: ComputedRef<boolean>
  tutorLoading: Readonly<Ref<boolean>>
  onTutorSearch: (query: string) => void
  onTutorLoadMore: (query: string) => void
  onTutorOpen: () => void
}

export const useFilterTutorBillingTutor = (): FilterTutorBillingTutorReturn => {
  const store = useTutorBillingStore()

  const selectedCache = ref<AppSelectOption<number>[]>([])

  const tutorIds = computed<number[] | undefined>({
    get: () => store.getFilters.tutorIds,
    set: (value) => store.setFilterTutorIds(value ?? []),
  })

  const fetcher = ({ query, page, signal }: AppSelectPaginatedFetcherParams): Promise<SelectPaginatedData | null> => {
    const ids = store.getFilters.tutorIds
    return useTutorsListApi({
      query,
      page,
      signal,
      preselected: ids.length ? [...ids] : undefined,
    })
  }

  const {
    options,
    hasMore: tutorHasMore,
    loading: tutorLoading,
    onSearch: onTutorSearch,
    onLoadMore: onTutorLoadMore,
    onOpen: onTutorOpen,
    refresh,
  } = useAppSelectPaginated(fetcher, { lazy: true })

  const tutorOptions = computed<AppSelectOption<number>[]>(() => options.value as AppSelectOption<number>[])

  const selectedTutorOptions = computed<AppSelectOption<number>[]>(() =>
    selectedCache.value.filter(option => store.getFilters.tutorIds.includes(option.value)),
  )

  const setCacheSelected = (): void => {
    const ids = store.getFilters.tutorIds
    const kept = selectedCache.value.filter(option => ids.includes(option.value))
    const added = tutorOptions.value.filter(option =>
      ids.includes(option.value) && !kept.some(item => item.value === option.value),
    )
    selectedCache.value = [...kept, ...added]
  }

  const setMissingLabelsLoaded = (ids: number[]): void => {
    const known = [...tutorOptions.value, ...selectedCache.value].map(option => option.value)
    if (ids.some(id => !known.includes(id))) void refresh()
  }

  watch([() => store.getFilters.tutorIds, tutorOptions], setCacheSelected, { immediate: true })

  watch(() => store.getFilters.tutorIds, setMissingLabelsLoaded, { immediate: true })

  return {
    tutorIds,
    tutorOptions,
    selectedTutorOptions,
    tutorHasMore,
    tutorLoading,
    onTutorSearch,
    onTutorLoadMore,
    onTutorOpen,
  }
}
